<script setup>
import axios from 'axios';
import { computed, ref } from "vue";
import { axiosCatchHandler } from "../../../modules/sharedGlobal";
import { onClickOutside } from "@vueuse/core";

const props = defineProps({
  organisationId: {
    type: Number,
    required: true,
  },
  website: {
    type: Object,
    required: true,
  },
})

const showDropdown = ref(false);
const dropdown = ref(null);
const prices = ref(null);
const loading = ref(false);
const searchString = ref('');

onClickOutside(dropdown, () => showDropdown.value === true && toggleDropdown());

const priceStripeId = ref(props.website.price?.stripe_id ?? '');
const priceId = ref(props.website.price?.id ?? '');
const productName = ref(props.website.price?.product.name ?? '');

const filteredPrices = computed(() => {
  return prices.value.filter(option =>
      option.name.toLowerCase().includes(searchString.value.toLowerCase()) ||
      option.formatted_price.includes(searchString.value) ||
      option.stripe_id.toLowerCase().includes(searchString.value.toLowerCase())
  );
});

const fetchPrices = async () => {
  try {
    loading.value = true;
    const { data } = await axios.get(`/admin/crm/organisations/${props.organisationId}/prices`);

    prices.value = data.prices;
  } catch (error) {
    axiosCatchHandler(error, () => {});
    loading.value = false;
  }
  loading.value = false;
}

const toggleDropdown = () => {
  searchString.value = '';
  showDropdown.value = !showDropdown.value;
}

const handleEdit = async () => {
  if (loading.value) {
    return;
  }

  if (prices.value === null) {
    await fetchPrices();
  }

  toggleDropdown();
}

const updatePrice = async (price) => {
  try {
    const { data } = await axios.post(`/admin/crm/organisations/${props.organisationId}/websites/${props.website.id}/price/update`, {
      price: price.id,
    });

    priceStripeId.value = data.website.price.stripe_id;
    priceId.value = data.website.price.id;
    productName.value = data.website.price.product.name;
  } catch (error) {
    axiosCatchHandler(error, () => {});
  }

  toggleDropdown();
}

</script>

<template>
  <div v-if="!showDropdown">
    <span v-if="priceId && priceStripeId && productName">
      <small>{{ priceStripeId }}</small><br>
      {{ productName }}
      <small>(Hike ID: {{ priceId }})</small>
    </span>

    <span v-else>
      <span class="text-danger">n/a</span>
    </span>

    <span class="ml-2 pointer">
      <i
        role="button"
        class="far fa-edit mr-1"
        @click="handleEdit"
      />
    </span>
  </div>

  <div
    v-else
    ref="dropdown"
    class="dropdown"
  >
    <input
      v-model="searchString"
      class="form-control"
      placeholder="Search"
    >
    <div class="dropdown-options">
      <div
        v-for="(option, index) in filteredPrices"
        :key="index"
        class="dropdown-option"
        :class="{
          'selected': option.stripe_id === priceStripeId
        }"
        @click="updatePrice(option)"
      >
        {{ option.name }}{{ !!option.price_name ? ': ' + option.price_name : '' }}
        <strong>({{ option.formatted_price }})</strong> <br>
        <small>({{ option.stripe_id }}) </small>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dropdown {
  position: relative;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #d4deee;
  z-index: 9999;
  min-width: 300px;
}

.dropdown-options {
  position: absolute;
  background: #ffffff;
  max-height: 300px;
  overflow: scroll;
  border-radius: 6px;
  border: 1px solid #d4deee;
  min-width: 300px;
}

.dropdown-option {
  padding: 0.5rem;
  text-align: left;
}

.dropdown-option:hover {
  background: #d4deee;
  cursor: pointer;
}

.selected {
  background: #c2c6cc;
}
</style>
